import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ClipboardList,
    Clock,
    User,
    MapPin,
    ArrowRight,
    MessageSquare,
    Eye,
    TrendingUp,
    PlayCircle,
    UserPlus,
    CheckCircle,
    AlertTriangle,
    Hash,
    Search,
    CalendarCheck,
    History
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTickets } from '../../contexts/TicketContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { useInfiniteTickets } from '../../hooks/useInfiniteTickets';

const StaffYourTickets = () => {
    const { tickets } = useTickets();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');

    // Filtros e Busca persistentes
    const [filters, setFilters] = useState(() => {
        if (!user?.discordId) return {
            department: 'all',
            order: 'newest',
            city: 'all',
            priority: 'all',
            status: 'all'
        };
        const saved = localStorage.getItem(`staff_pref_mine_${user.discordId}`);
        return saved ? JSON.parse(saved) : {
            department: 'all',
            order: 'newest',
            city: 'all',
            priority: 'all',
            status: 'all'
        };
    });

    const {
        tickets: myTickets,
        loading,
        loadingMore,
        hasMore,
        loadMore
    } = useInfiniteTickets({
        queryBuilder: (query) => {
            // BASE LOGIC: Assigned to me OR Escalated by me
            if (user?.id) {
                query = query.eq('assigned_to', user.id);
            } else {
                // Fallback to prevent load if user not ready
                query = query.eq('id', '0');
            }

            // Filters
            if (filters.department !== 'all') query = query.eq('category', filters.department);
            if (filters.city !== 'all') query = query.eq('city', filters.city);

            if (filters.priority !== 'all') {
                if (filters.priority === 'normal') {
                    query = query.or('priority.eq.normal,priority.eq.media');
                } else {
                    query = query.eq('priority', filters.priority);
                }
            }

            if (filters.status !== 'all') query = query.eq('status', filters.status);

            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                query = query.or(`title.ilike.%${term}%,id.ilike.%${term}%,author_name.ilike.%${term}%`);
            }

            if (filters.order === 'newest') {
                query = query.order('last_update', { ascending: false });
            } else {
                query = query.order('last_update', { ascending: true });
            }

            return query;
        },
        pageSize: 20,
        enabled: !!user?.id
    }, [filters, searchTerm, user?.id]);



    // Salvar preferências
    useEffect(() => {
        if (user?.discordId) {
            localStorage.setItem(`staff_pref_mine_${user.discordId}`, JSON.stringify(filters));
        }
    }, [filters, user]);


    // Categorias disponíveis
    const categories = [
        { id: 'suporte_duvidas', label: 'Dúvidas Gerais', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
        { id: 'compras_doacoes', label: 'Compras & Doações', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
        { id: 'cupons_descontos', label: 'Cupons & Descontos', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
        { id: 'denuncia_jogador', label: 'Denúncia Jogador', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
        { id: 'contestar_punicao', label: 'Revogar Punição', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
        { id: 'programacao', label: 'Problemas Técnicos', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
        { id: 'denuncia_staff', label: 'Denúncia Staff', color: 'bg-rose-500/20 text-rose-400 border-rose-500/30' }
    ];

    const getCategoryDetails = (categoryId) => {
        if (categoryId === 'ticket_automatico') return { label: 'Automático', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
        const category = categories.find(c => c.id === categoryId);
        return category || { label: categoryId, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
    };

    const getStatusBadgeColor = (status) => {
        const colors = {
            'aberto': 'bg-green-500/20 text-green-400 border-green-500/30',
            'aguardando_sistema': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
            'fechado': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
            'aguardando_usuario': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            'aguardando_staff': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            'em_andamento': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
            'escalado': 'bg-red-500/20 text-red-400 border-red-500/30',
            'resolvido': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
            'controle_qualidade': 'bg-blue-500/20 text-blue-400 border-blue-500/30'
        };
        return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    };

    const getStatusLabel = (status) => {
        const labels = {
            'aberto': 'Aberto',
            'aguardando_sistema': 'Verificando',
            'fechado': 'Resolvido',
            'aguardando_usuario': 'Aguard. Você',
            'aguardando_staff': 'Aguard. Staff',
            'em_andamento': 'Em Andamento',
            'escalado': 'Escalado',
            'resolvido': 'Resolvido',
            'controle_qualidade': 'C. Qualidade'
        };
        return labels[status] || status.replace(/_/g, ' ');
    };

    const getPriorityBadgeColor = (priority) => {
        const colors = {
            'alta': 'bg-red-500/20 text-red-400 border-red-500/30',
            'media': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            'baixa': 'bg-green-500/20 text-green-400 border-green-500/30',
            'normal': 'bg-blue-500/20 text-blue-400 border-blue-500/30'
        };
        return colors[priority] || colors['media'];
    };

    // Gera uma cor consistente baseada no nome da cidade
    const getCityColor = (cityName) => {
        if (!cityName) return 'bg-gray-900/50 text-gray-400 border-gray-700';
        const colors = [
            'bg-pink-500/10 text-pink-400 border-pink-500/20',
            'bg-violet-500/10 text-violet-400 border-violet-500/20',
            'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
            'bg-amber-500/10 text-amber-400 border-amber-500/20',
            'bg-lime-500/10 text-lime-400 border-lime-500/20',
            'bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/20',
            'bg-sky-500/10 text-sky-400 border-sky-500/20',
        ];
        let hash = 0;
        for (let i = 0; i < cityName.length; i++) {
            hash = cityName.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    const formatRelativeTime = (dateString, addSuffix = true) => {
        if (!dateString) return 'Data desconhecida';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Data inválida';

        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));

        if (diffInMinutes < 1) return 'Agora';
        if (diffInMinutes < 60) return `${diffInMinutes}min${addSuffix ? ' atrás' : ''}`;

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h${addSuffix ? ' atrás' : ''}`;

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays}d${addSuffix ? ' atrás' : ''}`;

        const diffInWeeks = Math.floor(diffInDays / 7);
        return `${diffInWeeks}sem${addSuffix ? ' atrás' : ''}`;
    };

    // Logic moved to hook


    // Lista de Cidades (Fixa)
    const availableCities = ['CAPITAL', 'VALLEY NORTE', 'MORADA DO VALLEY', 'VALLEY SUL'];

    // Componente de Filtro Dropdown
    const FilterDropdown = ({ label, value, onChange, options, icon: Icon }) => (
        <div className="flex flex-col gap-2 flex-1 min-w-[140px]">
            <label className="text-[10px] uppercase font-bold text-gray-500 tracking-widest flex items-center gap-1.5 pl-1">
                {Icon && <Icon className="w-3 h-3 text-blue-400/70" />}
                {label}
            </label>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger className="w-full bg-white/5 border-white/10 text-gray-200 h-11 rounded-xl hover:bg-white/10 hover:border-blue-500/30 hover:text-white transition-all focus:ring-0 focus:ring-offset-0">
                    <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-900/95 border-gray-800 backdrop-blur-xl text-gray-200">
                    {options.map(opt => (
                        <SelectItem
                            key={opt.value}
                            value={opt.value}
                            className="focus:bg-blue-600/20 focus:text-blue-200 cursor-pointer"
                        >
                            {opt.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );

    const TicketCard = ({ ticket }) => {
        const categoryDetails = getCategoryDetails(ticket.category);
        const displayContent = ticket.description || (ticket.responses?.[0]?.content) || 'Sem descrição informada.';
        const responseCount = ticket.responses ? ticket.responses.length : 0;
        const isAssignedToMe = ticket.assignedTo === user?.id;

        const assignee = ticket.assignees && ticket.assignees.length > 0
            ? ticket.assignees[ticket.assignees.length - 1]
            : null;

        const assigneeName = assignee?.name || assignee?.codename || ticket.assignedName || 'Staff';
        const assigneeDiscordId = assignee?.discordId;
        const assigneeAvatar = assignee?.avatar || ticket.assignedAvatar;

        const avatarUrl = assigneeDiscordId && assigneeAvatar && !assigneeAvatar.startsWith('http')
            ? `https://cdn.discordapp.com/avatars/${assigneeDiscordId}/${assigneeAvatar}.png`
            : assigneeAvatar;

        return (
            <motion.div
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={`group relative overflow-hidden rounded-2xl border transition-all duration-300 bg-white/[0.03] border-white/10 hover:border-white/20 hover:bg-white/[0.05]`}
                onClick={() => navigate(`/staff/tickets/${ticket.id}`)}
            >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-purple-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative p-5">
                    <div className="flex flex-wrap items-center justify-between gap-y-3 gap-x-4 mb-4">
                        {/* LADO ESQUERDO */}
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="flex flex-col mr-2">
                                <span className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-0.5">Ticket ID</span>
                                <span className="text-white font-mono text-sm font-bold">#{ticket.id}</span>
                            </div>
                            <div className="h-8 w-[1px] bg-white/10 mx-1 hidden sm:block" />

                            <Badge className={`${categoryDetails.color} border-none px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-md`}>
                                {categoryDetails.label}
                            </Badge>

                            {ticket.city && (
                                <Badge variant="outline" className={`ml-1 ${getCityColor(ticket.city)} px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md flex items-center gap-1 shadow-sm`}>
                                    <MapPin className="w-3 h-3" />
                                    {ticket.city}
                                </Badge>
                            )}

                            {/* PRIORIDADE E STATUS */}
                            <div className={`flex items-center space-x-1.5 px-2 py-1 rounded-md border ${getPriorityBadgeColor(ticket.priority)}`}>
                                <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${ticket.priority === 'alta' ? 'bg-red-400' : ticket.priority === 'media' ? 'bg-yellow-400' : 'bg-green-400'}`} />
                                <span className="text-[10px] font-bold uppercase tracking-tight">{ticket.priority === 'normal' ? 'Normal' : (ticket.priority || 'Média')}</span>
                            </div>

                            <Badge className={`text-[10px] px-3 py-1 font-bold uppercase tracking-wider ${getStatusBadgeColor(ticket.status)} border-none rounded-full shadow-lg shadow-black/20`}>
                                {getStatusLabel(ticket.status)}
                            </Badge>
                        </div>

                        {/* LADO DIREITO */}
                        <div className="flex flex-wrap items-center justify-end gap-3 flex-1 lg:flex-none">
                            <div className="flex items-center gap-3 text-[10px] text-gray-500">
                                <span className="flex items-center" title="Criado em">
                                    <CalendarCheck className="h-3 w-3 mr-1 opacity-50" />
                                    {formatRelativeTime(ticket.created_at || ticket.createdAt)}
                                </span>

                                {(ticket.lastUpdate || ticket.createdAt) && (
                                    <span className="flex items-center text-blue-400/80" title="Última atualização">
                                        <History className="h-3 w-3 mr-1 opacity-70" />
                                        {formatRelativeTime(ticket.updated_at || ticket.updatedAt || ticket.last_update || ticket.lastUpdate || ticket.created_at || ticket.createdAt, false)}
                                    </span>
                                )}

                                <span className="flex items-center" title="Mensagens">
                                    <MessageSquare className="h-3 w-3 mr-1 opacity-50" />
                                    {responseCount}
                                </span>
                            </div>

                            <div className="pl-2 border-l border-white/10">
                                {isAssignedToMe ? (
                                    <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full pl-1 pr-2 py-0.5">
                                        <Avatar className="h-5 w-5 border border-blue-500/30">
                                            <AvatarImage src={user?.avatar ? `https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png` : undefined} />
                                            <AvatarFallback className="bg-blue-900 text-blue-200 text-[8px]">{user?.name?.substring(0, 2).toUpperCase()}</AvatarFallback>
                                        </Avatar>
                                        <span className="text-[9px] font-bold uppercase tracking-wider text-blue-400">VOCÊ</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 bg-gray-800/50 border border-gray-700/50 rounded-full pl-1 pr-2 py-0.5">
                                        <Avatar className="h-5 w-5 border border-gray-600">
                                            <AvatarImage src={avatarUrl} />
                                            <AvatarFallback className="bg-gray-700 text-gray-300 text-[8px]">
                                                {assigneeName.substring(0, 2).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="text-[9px] font-medium text-gray-300 max-w-[80px] truncate">
                                            {assigneeName}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center text-gray-400 group-hover:text-white transition-colors cursor-pointer">
                                <Eye className="h-4 w-4 transform group-hover:scale-110 transition-transform" />
                            </div>
                        </div>
                    </div>

                    <div className="mb-1">
                        <div className="flex justify-between items-start">
                            <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors duration-300 line-clamp-1 mb-1 flex-1 mr-4">
                                {ticket.title || 'Solicitação sem título'}
                            </h3>
                        </div>
                        <p className="text-sm text-gray-400 line-clamp-2 leading-relaxed opacity-80 group-hover:opacity-100 transition-opacity">
                            {displayContent}
                        </p>
                    </div>
                </div>

                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-purple-600" />
            </motion.div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-white">Seus Tickets</h1>
                    <p className="text-gray-400 mt-1">Tickets sob sua responsabilidade ou interação</p>
                </div>

                {/* PAINEL DE CONTROLE DE FILTROS E BUSCA */}
                <div className="relative group rounded-3xl p-[1px] bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-blue-500/20">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 blur-xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative bg-black/40 backdrop-blur-xl rounded-3xl p-5 md:p-6 space-y-6">

                        {/* Barra de Busca Principal */}
                        <div className="relative z-10">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Search className="h-5 w-5 text-blue-400/80" />
                            </div>
                            <Input
                                type="text"
                                placeholder="Buscar por ID, Personagem, Discord, Cidade, Categoria ou Título..."
                                className="pl-12 h-14 bg-white/5 border-white/10 text-white focus:border-blue-500/50 focus:bg-white/10 focus:ring-4 focus:ring-blue-500/10 transition-all rounded-xl text-base shadow-lg shadow-black/20"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors group/clear"
                                >
                                    <div className="bg-white/10 group-hover/clear:bg-white/20 rounded-full p-1.5 transition-colors">
                                        <ArrowRight className="h-3 w-3 rotate-45" />
                                    </div>
                                </button>
                            )}
                        </div>

                        {/* Grid de Filtros */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            <FilterDropdown
                                label="Departamento"
                                value={filters.department}
                                onChange={(val) => setFilters(prev => ({ ...prev, department: val }))}
                                options={[
                                    { value: 'all', label: 'Todos' },
                                    ...categories.map(c => ({ value: c.id, label: c.label }))
                                ]}
                                icon={Hash}
                            />
                            <FilterDropdown
                                label="Cidade"
                                value={filters.city}
                                onChange={(val) => setFilters(prev => ({ ...prev, city: val }))}
                                options={[
                                    { value: 'all', label: 'Todas' },
                                    ...availableCities.map(c => ({ value: c, label: c }))
                                ]}
                                icon={MapPin}
                            />
                            <FilterDropdown
                                label="Prioridade"
                                value={filters.priority}
                                onChange={(val) => setFilters(prev => ({ ...prev, priority: val }))}
                                options={[
                                    { value: 'all', label: 'Todas' },
                                    { value: 'alta', label: 'Alta' },
                                    { value: 'normal', label: 'Normal' },
                                    { value: 'baixa', label: 'Baixa' }
                                ]}
                                icon={TrendingUp}
                            />
                            <FilterDropdown
                                label="Status"
                                value={filters.status}
                                onChange={(val) => setFilters(prev => ({ ...prev, status: val }))}
                                options={[
                                    { value: 'all', label: 'Todos' },
                                    { value: 'aberto', label: 'Aberto' },
                                    { value: 'aguardando_staff', label: 'Aguard. Staff' },
                                    { value: 'aguardando_usuario', label: 'Aguard. Usuário' },
                                    { value: 'aguardando_sistema', label: 'Aguard. Sistema' },
                                    { value: 'em_andamento', label: 'Em Andamento' },
                                    { value: 'escalado', label: 'Escalado' },
                                    { value: 'controle_qualidade', label: 'Controle de Qualidade' },
                                    { value: 'resolvido', label: 'Resolvido' },
                                    { value: 'fechado', label: 'Fechado' }
                                ]}
                                icon={CheckCircle}
                            />
                            <FilterDropdown
                                label="Ordem"
                                value={filters.order}
                                onChange={(val) => setFilters(prev => ({ ...prev, order: val }))}
                                options={[
                                    { value: 'newest', label: 'Mais Recentes' },
                                    { value: 'oldest', label: 'Mais Antigos' }
                                ]}
                                icon={Clock}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid gap-4">
                {myTickets.length > 0 ? (
                    <>
                        {myTickets.map((ticket) => (
                            <TicketCard key={ticket.id} ticket={ticket} />
                        ))}
                        {hasMore && (
                            <div className="flex justify-center pt-4 pb-8">
                                <Button
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    variant="outline"
                                    className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                                >
                                    {loadingMore ? 'Carregando...' : 'Carregar Mais'}
                                </Button>
                            </div>
                        )}
                    </>
                ) : (
                    !loading && (
                        <div className="text-center py-12 bg-gray-900/20 rounded-2xl border border-gray-800">
                            <ClipboardList className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                            <h3 className="text-xl font-medium text-white">Nenhum ticket encontrado</h3>
                            <p className="text-gray-400">Você não tem tickets correspondentes aos filtros.</p>
                            {(searchTerm || filters.department !== 'all' || filters.city !== 'all') && (
                                <Button
                                    variant="outline"
                                    className="mt-4 border-gray-700 bg-transparent text-gray-300 hover:bg-gray-800 hover:text-white"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setFilters({
                                            department: 'all',
                                            order: 'newest',
                                            city: 'all',
                                            priority: 'all',
                                            status: 'all'
                                        });
                                    }}
                                >
                                    Limpar Filtros
                                </Button>
                            )}
                        </div>
                    )
                )}
                {loading && myTickets.length === 0 && (
                    <div className="text-center py-20 text-gray-400">Carregando seus tickets...</div>
                )}
            </div>
        </div>
    );
};

export default StaffYourTickets;
